import { resolveBusInput } from '@/audio/busRegistry'
import { ensureMeter, disposeMeter } from '@/audio/meters'
import {
  useSequencerStore,
  selectCurrentContainer,
} from '@/stores/sequencerStore'
import {
  useAudioDevicesStore,
  DEFAULT_INPUT_ID,
} from '@/stores/audioDevicesStore'
import {
  hasWebGetUserMedia,
  monitorUnavailableReason,
} from '@/audio/capabilities'
import { hasNativeAudioPlugin } from '@/lib/nativeAudio'
import {
  ensureNativeInputStream,
  type NativeInputStream,
} from '@/audio/NativeInputStream'
import type { InputSource } from '@busswerks/shared'

/**
 * P5c multi-channel input router. Owns per-device MediaStreams +
 * ChannelSplitterNodes so multiple `InputSource` rows that share a
 * device reuse the same capture (one getUserMedia per device, not
 * per source). Each source's chosen channel feeds a small mono
 * gain node routed to its target bus.
 *
 *   device  →  MediaStreamSourceNode  →  ChannelSplitterNode(N)
 *                                        ├─ [0] → sourceA.gain → busA
 *                                        ├─ [1] → sourceB.gain → busA
 *                                        └─ [2] → sourceC.gain → busC
 *
 * Gated on the existing `inputMonitorEnabled` flag in
 * `audioDevicesStore` — one master "input live" switch controls
 * both the legacy `AudioInput` single-stream monitor AND the
 * per-source multi-channel pipeline. Keeps the permission gesture
 * to a single toggle; stored sources replay transparently once the
 * user flips monitoring back on.
 *
 * Errors (permission denied, channel count mismatch, device gone)
 * surface via `subscribe` so the AudioPanel can show per-source
 * status without blocking unrelated sources — one broken source
 * doesn't silence the others.
 */

interface DeviceCapture {
  /** Kind tag so disposeCapture can branch between tearing down a
   *  MediaStream (web) vs. leaving the NativeInputStream alone
   *  (native — it's shared with the legacy Monitor path). */
  kind: 'web' | 'native'
  splitter: ChannelSplitterNode
  channelCount: number
  /** Web capture owns its MediaStream + source node; native capture
   *  piggybacks on the shared `NativeInputStream` singleton + its
   *  worklet output, so these fields are only populated on web. */
  stream?: MediaStream
  source?: MediaStreamAudioSourceNode
  /** Native capture keeps a reference to the shared stream so
   *  `disposeCapture` can signal it to stop when no sources remain
   *  AND the legacy Monitor path isn't also using it. Web leaves
   *  this undefined. */
  nativeStream?: NativeInputStream
  /** Source ids currently consuming this capture. When empty we
   *  tear down the capture to free the mic indicator. */
  refs: Set<string>
}

interface SourceRouting {
  sourceId: string
  deviceId: string
  channelIndex: number
  /** 1 = mono (splitter[ch] → gain → bus); 2 = stereo (splitter[ch]
   *  → merger.L, splitter[ch+1] → merger.R → gain → bus). */
  channelCount: 1 | 2
  busId: string
  /** Terminal gain feeding the bus. Disconnect this to tear the
   *  routing down — every upstream node is owned either by the
   *  shared splitter (kept alive for other sources) or by the
   *  routing-local merger (torn down alongside the gain). */
  gainNode: GainNode
  /** Merger owned by stereo routings. Null for mono. */
  merger: ChannelMergerNode | null
  connectedTo: AudioNode | null
}

interface RouterListener {
  onError?: (sourceId: string, message: string) => void
  onCaptureChange?: (deviceId: string, channelCount: number) => void
}

export class MultiInputRouter {
  private ctx: AudioContext
  private captures = new Map<string, DeviceCapture>()
  private routings = new Map<string, SourceRouting>()
  private listeners = new Set<RouterListener>()
  /** In-flight device captures keyed by deviceId. Prevents
   *  duplicate getUserMedia calls when two sources sharing the
   *  same device arrive in the same sync tick. */
  private pendingCaptures = new Map<string, Promise<DeviceCapture>>()

  constructor(ctx: AudioContext) {
    this.ctx = ctx
  }

  subscribe(listener: RouterListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** Snapshot of per-device channel counts the router has observed.
   *  UI uses this to populate the channel dropdown in the
   *  AudioPanel's input-sources list — on web the only way to
   *  know channel count is to actually open the stream. */
  getCaptureChannelCount(deviceId: string): number {
    return this.captures.get(deviceId)?.channelCount ?? 0
  }

  /** Apply the given source list atomically. Removes routings that
   *  no longer exist, adds new ones, updates changed ones. Safe to
   *  call repeatedly on every store tick. */
  async sync(sources: InputSource[], enabled: boolean): Promise<void> {
    if (!enabled) {
      // Tear everything down when monitoring turns off — the user
      // expects the mic indicator to clear.
      this.disposeAll()
      return
    }
    const seen = new Set<string>()
    for (const src of sources) {
      seen.add(src.id)
      try {
        await this.ensureRouting(src)
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Input permission denied'
        for (const l of this.listeners) l.onError?.(src.id, msg)
      }
    }
    for (const [sourceId] of this.routings) {
      if (!seen.has(sourceId)) this.removeRouting(sourceId)
    }
  }

  /** Force-close all captures + routings. Called when monitoring
   *  flips off or the router is torn down. */
  dispose(): void {
    this.disposeAll()
  }

  private disposeAll(): void {
    for (const sourceId of [...this.routings.keys()]) {
      this.removeRouting(sourceId)
    }
    // removeRouting tears down captures when refs drop to 0, but be
    // defensive in case a capture is dangling without routings
    // (shouldn't happen, but no harm in belt + suspenders).
    for (const deviceId of [...this.captures.keys()]) {
      this.disposeCapture(deviceId)
    }
  }

  private async ensureRouting(src: InputSource): Promise<void> {
    const desiredCount: 1 | 2 = src.channelCount === 2 ? 2 : 1
    const existing = this.routings.get(src.id)
    if (
      existing &&
      existing.deviceId === src.deviceId &&
      existing.channelIndex === src.channelIndex &&
      existing.channelCount === desiredCount &&
      existing.busId === src.busId
    ) {
      // No-op — store pushed out an unrelated change. Still make
      // sure the bus destination hasn't moved (user renamed / the
      // target bus got recreated).
      const dest = resolveBusInput(src.busId)
      if (dest && dest !== existing.connectedTo) {
        if (existing.connectedTo) {
          try {
            existing.gainNode.disconnect(existing.connectedTo)
          } catch {
            /* already disconnected */
          }
        }
        existing.gainNode.connect(dest)
        existing.connectedTo = dest
      }
      return
    }
    if (existing) this.removeRouting(src.id)

    const capture = await this.ensureCapture(src.deviceId)
    // Clamp channelIndex to the capture's actual count. Stored
    // channelIndex can outrun the device (user disconnected a
    // bigger interface); the UI shows an out-of-range warning but
    // we don't throw. Stereo pairs need TWO consecutive channels so
    // we clamp differently: max allowed start is channelCount - 2.
    const maxStart =
      desiredCount === 2
        ? Math.max(0, capture.channelCount - 2)
        : Math.max(0, capture.channelCount - 1)
    const channelIndex = Math.max(0, Math.min(src.channelIndex, maxStart))
    const gain = this.ctx.createGain()
    gain.gain.value = 1
    let merger: ChannelMergerNode | null = null
    if (desiredCount === 2 && capture.channelCount >= 2) {
      merger = this.ctx.createChannelMerger(2)
      capture.splitter.connect(merger, channelIndex, 0)
      capture.splitter.connect(merger, channelIndex + 1, 1)
      merger.connect(gain)
    } else {
      // Mono path — one splitter output feeds the gain directly.
      // The bus's downstream panner upmixes mono → stereo naturally.
      capture.splitter.connect(gain, channelIndex, 0)
    }
    // Insert a meter tap on the gain so the UI can render a peak
    // bar per source without intruding on the signal chain. Analyser
    // is a passthrough that doesn't affect the audio routed to the
    // bus — it just snoops whatever is flowing through `gain`.
    const analyser = ensureMeter(this.ctx, src.id)
    gain.connect(analyser)
    const dest = resolveBusInput(src.busId)
    if (dest) gain.connect(dest)
    capture.refs.add(src.id)
    this.routings.set(src.id, {
      sourceId: src.id,
      deviceId: src.deviceId,
      channelIndex,
      channelCount: desiredCount,
      busId: src.busId,
      gainNode: gain,
      merger,
      connectedTo: dest,
    })
  }

  private async ensureCapture(deviceId: string): Promise<DeviceCapture> {
    const existing = this.captures.get(deviceId)
    if (existing) return existing
    const pending = this.pendingCaptures.get(deviceId)
    if (pending) return pending
    const promise = this.openCapture(deviceId)
    this.pendingCaptures.set(deviceId, promise)
    try {
      const capture = await promise
      return capture
    } finally {
      this.pendingCaptures.delete(deviceId)
    }
  }

  private async openCapture(deviceId: string): Promise<DeviceCapture> {
    // P10 native branch — per-device NativeInputStream. The
    // registry returns a dedicated instance per deviceId so
    // concurrent sources bound to different hardware don't steal
    // each other's frames (NativeInputStream filters incoming
    // events by its own deviceId tag). The native engine still
    // serialises start() calls today; proper multi-engine native
    // support is a follow-up, but the keyed JS architecture is
    // ready for it.
    if (hasNativeAudioPlugin()) {
      const stream = ensureNativeInputStream(this.ctx, deviceId)
      await stream.start()
      const channelCount = Math.max(1, stream.getChannelCount())
      const node = stream.getOutputNode()
      if (!node) {
        throw new Error('Native input stream did not expose an output node')
      }
      const splitter = this.ctx.createChannelSplitter(channelCount)
      node.connect(splitter)
      const capture: DeviceCapture = {
        kind: 'native',
        splitter,
        channelCount,
        nativeStream: stream,
        refs: new Set(),
      }
      this.captures.set(deviceId, capture)
      for (const l of this.listeners)
        l.onCaptureChange?.(deviceId, channelCount)
      return capture
    }
    // Pre-flight guard: on Mac Catalyst `navigator.mediaDevices` is
    // undefined even with mic entitlements, and the raw TypeError
    // leaks to the UI as a confusing "undefined is not an object".
    // Fail fast with a real explanation so the user can distinguish
    // "platform doesn't support this" from "my settings are wrong".
    if (!hasWebGetUserMedia()) {
      throw new Error(
        monitorUnavailableReason() ?? 'Input monitoring not available',
      )
    }
    // `deviceId: { exact }` pins to the chosen device. Empty string
    // / DEFAULT_INPUT_ID fall through to the browser default so the
    // "system default" sentinel is a real audio path rather than a
    // no-op placeholder.
    const useDefault = !deviceId || deviceId === DEFAULT_INPUT_ID
    const audio: MediaTrackConstraints = useDefault
      ? {}
      : { deviceId: { exact: deviceId } }
    audio.echoCancellation = false
    audio.noiseSuppression = false
    audio.autoGainControl = false
    // Ask for up to 32 channels; browsers cap this at the device's
    // real capacity silently. No channelCount: exact because Chrome
    // rejects interfaces that don't match the exact count.
    audio.channelCount = 32
    const stream = await navigator.mediaDevices.getUserMedia({ audio })
    const source = this.ctx.createMediaStreamSource(stream)
    // Discrete channels keep the splitter mapping 1:1. Without this
    // the browser may down-mix a multi-channel input to stereo
    // before we get our hands on it.
    source.channelInterpretation = 'discrete'
    // Resolve the actual channel count from the source node (more
    // reliable than track settings, which frequently report 1 even
    // for multi-channel devices).
    const trackSettings = stream.getAudioTracks()[0]?.getSettings()
    const channelCount = Math.max(
      1,
      trackSettings?.channelCount ?? source.channelCount ?? 2,
    )
    const splitter = this.ctx.createChannelSplitter(channelCount)
    source.connect(splitter)
    const capture: DeviceCapture = {
      kind: 'web',
      stream,
      source,
      splitter,
      channelCount,
      refs: new Set(),
    }
    this.captures.set(deviceId, capture)
    for (const l of this.listeners) l.onCaptureChange?.(deviceId, channelCount)
    return capture
  }

  private removeRouting(sourceId: string): void {
    const routing = this.routings.get(sourceId)
    if (!routing) return
    // Drop the meter first so a stale analyser doesn't read
    // half-disconnected data during the tear-down.
    disposeMeter(sourceId)
    try {
      routing.gainNode.disconnect()
    } catch {
      /* already disconnected */
    }
    if (routing.merger) {
      // Merger sits upstream of the gain; disconnecting it releases
      // the splitter→merger edges too (splitter itself stays alive
      // for other sources).
      try {
        routing.merger.disconnect()
      } catch {
        /* already disconnected */
      }
    }
    this.routings.delete(sourceId)
    const capture = this.captures.get(routing.deviceId)
    if (capture) {
      capture.refs.delete(sourceId)
      if (capture.refs.size === 0) this.disposeCapture(routing.deviceId)
    }
  }

  private disposeCapture(deviceId: string): void {
    const capture = this.captures.get(deviceId)
    if (!capture) return
    // Web capture owns its MediaStream + source — fully dispose so
    // the mic indicator clears. Native capture shares the stream
    // with the legacy Monitor path (AudioInput.ts) so we only tear
    // down OUR splitter; the NativeInputStream stops itself when
    // nothing else references it (AudioInput handles its own stop).
    if (capture.kind === 'web') {
      if (capture.source) {
        try {
          capture.source.disconnect()
        } catch {
          /* already disconnected */
        }
      }
      if (capture.stream) {
        for (const t of capture.stream.getTracks()) t.stop()
      }
    }
    try {
      capture.splitter.disconnect()
    } catch {
      /* already disconnected */
    }
    this.captures.delete(deviceId)
    for (const l of this.listeners) l.onCaptureChange?.(deviceId, 0)
  }
}

// ── Singleton + store subscription ────────────────────────────────

let sharedRouter: MultiInputRouter | null = null

/** Idempotent initializer. Pulls `inputSources` off the current
 *  container and keeps the router in sync with the store. Monitoring
 *  state (`inputMonitorEnabled` in audioDevicesStore) gates whether
 *  captures are live — when OFF, routings are torn down so the mic
 *  indicator disappears between sessions. */
export function ensureMultiInput(ctx: AudioContext): MultiInputRouter {
  if (sharedRouter) return sharedRouter
  sharedRouter = new MultiInputRouter(ctx)
  const sync = (): void => {
    if (!sharedRouter) return
    const seqState = useSequencerStore.getState()
    const container = selectCurrentContainer(seqState)
    const sources = container?.inputSources ?? []
    const enabled = useAudioDevicesStore.getState().inputMonitorEnabled
    void sharedRouter.sync(sources, enabled)
  }
  sync()
  useSequencerStore.subscribe(sync)
  useAudioDevicesStore.subscribe(sync)
  return sharedRouter
}

export function getMultiInput(): MultiInputRouter | null {
  return sharedRouter
}
